import React, {useEffect, useMemo, useState} from "react";
import { 
    PhoneIcon, 
    XMarkIcon,
    UserPlusIcon,
    Cog6ToothIcon,
    PaperAirplaneIcon 
} from '@heroicons/react/24/outline';
import {format, parseISO, differenceInHours} from "date-fns";
import BookSession from "./BookSession";
import PopupModelWrapper from "./PopupModelWrapper";
import ConfirmCancelSession from "./ConfirmCancelSession";
import ManageSession from "./ManageSession";
import Helper from "../helpers";
import AddUserToSession from "./Modals/AddUserToSession";
import SessionPlacesCard from "./SessionPlaces";

const SessionView = (props) => {
    const {session, AdminView, Users} = props;
    // const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

    const [isAdmin, setIsAdmin] = useState(false);
    const [sessionUsers, setSessionUsers] = useState(Users);
    const [openAddBookingModal, setOpenAddBookingModal] = useState(false);
    const [openCancelBookingModal, setOpenCancelBookingModal] = useState(false);
    const [openManageSessionModal, setOpenManageSessionModal] = useState(false);
    const [openAddUserToSessionModal, setOpenAddUserToSessionModal] = useState(false);

    const [canCancelBooking, setCanCancelBooking] = useState(true);
    
    const handleCloseAddBookingModal = () => {
        setOpenAddBookingModal(false);

    }
    const handleCloseCancelBookingModal = () => {
        setOpenCancelBookingModal(false);
    }
    const handleCloseManageSessionModal = () => {
        setOpenManageSessionModal(false);
    }

    const handleCloseAddUserToSessionModal= () => {
        setOpenAddUserToSessionModal(false);
        setTimeout( () => {
            window.location.reload();
        },1800);
    }

    useMemo(() => {
        setSessionUsers(Users);
    }, [Users]);

    useEffect(() => {
        const checkAdmin = async () => {
            const admin = await Helper.userIsAdmin();
            setIsAdmin(admin);
        };
        checkAdmin();
        
        const cancelWindow = differenceInHours(new Date(session.startDate), new Date()) >= 2;
        setCanCancelBooking(cancelWindow);
    }, [session.startDate]);

    //const genericImageUrl = process.env.PUBLIC_URL + "/img/sessionGenerics/personal_session.jpeg";
    const genericImageUrl = process.env.PUBLIC_URL + "/img/insight_gym_long.jpg";

    return (
        <>
            <div className="mb-6 relative overflow-hidden rounded-2xl card-luxury shadow-luxury-lg min-h-[200px]">
                {/* Background Image */}
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url(${session.imageUrl || genericImageUrl})`,
                        backgroundPosition: '75% 50%',
                        filter: 'grayscale(100%) opacity(0.9) saturate(80%)'
                    }}
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-luxury-black/60 via-luxury-charcoal/50 to-luxury-black/60"></div>
                </div>

                {/* Content - More transparent overlay */}
                <div className="relative z-10 p-4 sm:p-6">
                    {/* Mobile Layout (< 768px) */}
                    <div className="block md:hidden">
                        {/* Mobile Header - Title and Date/Time */}
                        <div className="mb-4">
                            <h3 className="text-xl font-display font-bold text-gradient-emerald mb-2">{session.title}</h3>
                            <div className="flex items-center gap-3 ">
                                <div className="flex-1 grid grid-cols-3 gap-2">
                                    {session.sessionCost && (
                                <div className="glass-luxury rounded-lg p-2 text-center min-w-[60px] border border-emerald-500/30 bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/60">
                                <div className="text-xs text-emerald-300/80 uppercase font-display">
                                   <span className="text-xs text-emerald-300 uppercase">Cost</span>
                                   </div>
                                   <div className="text-lg font-bold text-white">
                                        <span className="text-sm font-bold text-white">{session.sessionCost}</span>
                                    </div>
                                    <div className="text-xs text-emerald-300/60 font-body">tokens</div>
                                </div>
                                )}
                                    <div className="bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/60">
                                        <SessionPlacesCard seatsTaken={session.seatsTaken} availableSeats={session.availableSeats} small={true} />
                                    </div>
                                    <div className="glass-luxury rounded-lg p-2 border border-accent-platinum/20 text-center bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/60">
                                        <div className="text-xs text-emerald-300/60 uppercase font-display">Duration</div>
                                        <div className="text-sm font-bold text-white">{session.numberOfMinuets}</div>
                                        <div className="text-xs text-emerald-600/50 font-body">min</div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-2">
                                <div className="glass-luxury rounded-xl p-3 border border-emerald-500/30  bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/60">
                                    <div className="flex items-center justify-between">
                                        {/* Left side - Month and Date (bigger) */}
                                        <div className="flex flex-col items-center">
                                            <div className="text-sm text-emerald-300/80 uppercase tracking-wide font-display">
                                                {format(parseISO(session.startDate), "MMM")}
                                            </div>
                                            <div className="text-3xl font-display font-bold text-emerald-200">
                                                {format(parseISO(session.startDate), "dd")}
                                            </div>
                                        </div>
                                        
                                        {/* Center/Right - Time (fills most of the space) */}
                                        <div className="flex-1 text-center ml-4">
                                            <div className="text-4xl font-display font-bold text-white">
                                                {format(parseISO(session.startDate), "HH:mm")}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                                                    </div>

                        {/* Mobile Actions */}
                        <div className="space-y-3">
                            {!AdminView && session.sessionBookText !== "Booked" && (
                                <button
                                    disabled={(session.availableSeats - session.seatsTaken) === 0 || differenceInHours(new Date(session.startDate), new Date()) < 24}
                                    onClick={() => setOpenAddBookingModal(true)}
                                    className={`w-full ${
                                        (session.availableSeats - session.seatsTaken) === 0 || differenceInHours(new Date(session.startDate), new Date()) < 24
                                            ? 'bg-luxury-graphite/50 text-accent-platinum/50 cursor-not-allowed border border-emerald-600/20 py-3 px-4 rounded-xl font-display font-semibold transition-all duration-300'
                                            : 'btn-primary'
                                    } flex items-center justify-center space-x-2`}
                                >
                                    {differenceInHours(new Date(session.startDate), new Date()) < 24 ? (
                                        <PhoneIcon className="h-5 w-5" />
                                    ) : (
                                        <PaperAirplaneIcon className="h-5 w-5" />
                                    )}
                                    <span>
                                        {(session.availableSeats - session.seatsTaken) === 0
                                            ? "Full"
                                            : differenceInHours(new Date(session.startDate), new Date()) < 24
                                                ? "Call to Book"
                                                : session.sessionBookText}
                                    </span>
                                </button>
                            )}
                            
                            {!AdminView && session.sessionBookText === "Booked" && canCancelBooking && (
                                <button
                                    onClick={() => setOpenCancelBookingModal(true)}
                                    className="w-full btn-secondary bg-orange-950/70 text-performance-pulse border-performance-pulse/50 hover:border-performance-pulse/80 hover:shadow-[0_0_30px_rgba(255,7,58,0.25)] flex items-center justify-center space-x-2"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                    <span>Cancel Session</span>
                                </button>
                            )}

                            {isAdmin && (
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => setOpenManageSessionModal(true)}
                                        className="btn-secondary py-2 px-3 rounded-xl flex items-center justify-center space-x-1"
                                    >
                                        <Cog6ToothIcon className="h-4 w-4" />
                                        <span>Manage</span>
                                    </button>
                                    
                                    <button
                                        onClick={() => setOpenAddUserToSessionModal(true)}
                                        className="btn-secondary py-2 px-3 rounded-xl flex items-center justify-center space-x-1"
                                    >
                                        <UserPlusIcon className="h-4 w-4" />
                                        <span>Add User</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Mobile Attendees */}
                        {sessionUsers && sessionUsers.length > 0 && (
                            <div className="mt-4">
                                <div className="glass-luxury rounded-xl border border-emerald-600/20 overflow-hidden">
                                    <div className="px-4 py-2 bg-luxury-charcoal/50 border-b border-emerald-600/10">
                                        <h4 className="text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wide">Attendees ({sessionUsers.length})</h4>
                                    </div>
                                    <div className="p-3 max-h-20 overflow-y-auto scrollbar-luxury">
                                        <div className="flex flex-wrap gap-2">
                                            {sessionUsers.map((user) => (
                                                <span key={user.userId} className="badge-luxury text-xs">
                                                    {user.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Tablet Layout (768px - 1024px) */}
                    <div className="hidden md:block lg:hidden">
                        <div className="flex flex-col gap-4">
                            {/* Tablet Header with Date/Time */}
                            <div className="flex items-start gap-4">
                                <div className="glass-luxury rounded-xl p-3 text-center min-w-[80px] border border-emerald-500/30">
                                    <div className="text-xs text-emerald-300/80 uppercase tracking-wide font-display">
                                        {format(parseISO(session.startDate), "MMM")}
                                    </div>
                                    <div className="text-3xl font-display font-bold text-emerald-200">
                                        {format(parseISO(session.startDate), "dd")}
                                    </div>
                                    <div className="text-xs text-emerald-600/60 font-body">
                                        {format(parseISO(session.startDate), "EEE")}
                                    </div>
                                    <div className="divider-luxury my-2"></div>
                                    <div className="text-xl font-display font-bold text-gradient-emerald">
                                        {format(parseISO(session.startDate), "HH:mm")}
                                    </div>
                                </div>
                                
                                <div className="flex-1">
                                    <h3 className="text-2xl font-display font-bold text-gradient-emerald mb-3">{session.title}</h3>
                                    
                                    {/* Tablet Info Cards */}
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/60 rounded-xl">
                                            <SessionPlacesCard seatsTaken={session.seatsTaken} availableSeats={session.availableSeats} />
                                        </div>
                                        
                                        <div className="glass-luxury rounded-xl p-3 border border-accent-platinum/20 text-center bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/60">
                                            <div className="text-xs text-emerald-300/60 uppercase font-display">Duration</div>
                                            <div className="text-lg font-display font-bold text-white">{session.numberOfMinuets}</div>
                                            <div className="text-xs text-emerald-600/60 font-body">minutes</div>
                                        </div>
                                        
                                        {session.sessionCost && (
                                            <div className="glass-luxury rounded-xl p-3 border border-emerald-500/30 text-center bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/60">
                                                <div className="text-xs text-emerald-300/80 uppercase font-display">Cost</div>
                                                <div className="text-lg font-display font-bold text-white">{session.sessionCost}</div>
                                                <div className="text-xs text-emerald-300 font-body">tokens</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Tablet Actions */}
                            <div className="flex gap-3">
                                {!AdminView && session.sessionBookText !== "Booked" && (
                                    <button
                                        disabled={(session.availableSeats - session.seatsTaken) === 0 || differenceInHours(new Date(session.startDate), new Date()) < 24}
                                        onClick={() => setOpenAddBookingModal(true)}
                                        className={`flex-1 ${
                                            (session.availableSeats - session.seatsTaken) === 0 || differenceInHours(new Date(session.startDate), new Date()) < 24
                                                ? 'bg-luxury-graphite/50 text-accent-platinum/50 cursor-not-allowed border border-emerald-600/20 py-3 px-6 rounded-xl font-display font-semibold transition-all duration-300'
                                                : 'btn-primary'
                                        } flex items-center justify-center space-x-2`}
                                    >
                                        {differenceInHours(new Date(session.startDate), new Date()) < 24 ? (
                                            <PhoneIcon className="h-5 w-5" />
                                        ) : (
                                            <PaperAirplaneIcon className="h-5 w-5" />
                                        )}
                                        <span>
                                            {(session.availableSeats - session.seatsTaken) === 0
                                                ? "Full"
                                                : differenceInHours(new Date(session.startDate), new Date()) < 24
                                                    ? "Call to Book"
                                                    : session.sessionBookText}
                                        </span>
                                    </button>
                                )}
                                
                                {!AdminView && session.sessionBookText === "Booked" && canCancelBooking && (
                                    <button
                                        onClick={() => setOpenCancelBookingModal(true)}
                                        className="flex-1 btn-secondary  bg-orange-950/70 text-performance-pulse border-performance-pulse/50 hover:border-performance-pulse/80 hover:shadow-[0_0_30px_rgba(255,7,58,0.25)] flex items-center justify-center space-x-2"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                        <span>Cancel Session</span>
                                    </button>
                                )}

                                {isAdmin && (
                                    <>
                                        <button
                                            onClick={() => setOpenManageSessionModal(true)}
                                            className="btn-secondary flex-1 py-3 px-4 rounded-xl flex items-center justify-center space-x-2"
                                        >
                                            <Cog6ToothIcon className="h-5 w-5" />
                                            <span>Manage</span>
                                        </button>
                                        
                                        <button
                                            onClick={() => setOpenAddUserToSessionModal(true)}
                                            className="btn-secondary flex-1 py-3 px-4 rounded-xl flex items-center justify-center space-x-2"
                                        >
                                            <UserPlusIcon className="h-5 w-5" />
                                            <span>Add User</span>
                                        </button>
                                    </>
                                )}
                            </div>
                            
                            {/* Tablet Attendees */}
                            {sessionUsers && sessionUsers.length > 0 && (
                                <div className="glass-luxury rounded-xl border border-emerald-600/20 overflow-hidden">
                                    <div className="px-4 py-2 bg-luxury-charcoal/50 border-b border-emerald-600/10">
                                        <h4 className="text-sm font-display font-semibold text-emerald-300/80 uppercase tracking-wide">Attendees ({sessionUsers.length})</h4>
                                    </div>
                                    <div className="p-3 max-h-32 overflow-y-auto scrollbar-luxury">
                                        <div className="flex flex-wrap gap-2">
                                            {sessionUsers.map((user) => (
                                                <span key={user.userId} className="badge-luxury text-xs">
                                                    {user.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden lg:flex gap-6">
                        {/* Left Side - Session Info with subtle calendar time */}
                        <div className="flex-1">
                            {/* Subtle Calendar Time Display */}
                            <div className="flex items-end gap-4 mb-4">
                                <div className="glass-luxury bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/90 rounded-xl p-4 text-center min-w-[90px] border border-emerald-500/30">
                                    <div className="text-xs text-emerald-300/80 uppercase tracking-wide font-display">
                                        {format(parseISO(session.startDate), "MMM")}
                                    </div>
                                    <div className="text-3xl font-display font-bold text-emerald-200">
                                        {format(parseISO(session.startDate), "dd")}
                                    </div>
                                    <div className="text-xs text-emerald-600/60 font-body">
                                        {format(parseISO(session.startDate), "EEE")}
                                    </div>
                                    <div className="divider-luxury my-2"></div>
                                    <div className="text-lg font-display font-bold text-white">
                                        {format(parseISO(session.startDate), "HH:mm")}
                                    </div>
                                </div>
                                
                                <div className="flex-1">
                                    <h3 className="text-2xl font-display font-bold text-gradient-emerald mb-4">{session.title}</h3>
                                    
                                    {/* Session Details Cards */}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {/* Places Card */}
                                        <div className="bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/60 rounded-xl">
                                            <SessionPlacesCard seatsTaken={session.seatsTaken} availableSeats={session.availableSeats} />
                                        </div>
                                        {/* Duration Card */}
                                        <div className="glass-luxury rounded-xl p-3 border border-accent-platinum/20 bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/60">
                                            <div className="text-xs text-emerald-300/60 uppercase tracking-wide mb-1 font-display">Duration</div>
                                            <div className="text-xl font-display font-bold text-white">
                                                {session.numberOfMinuets}
                                            </div>
                                            <div className="text-xs text-emerald-600/60 font-body">minutes</div>
                                        </div>
                                        
                                        {/* Cost Card */}
                                        {session.sessionCost && (
                                            <div className="glass-luxury rounded-xl p-3 border border-emerald-500/30 bg-gradient-to-br from-luxury-slate/90 to-luxury-obsidian/60">
                                                <div className="text-xs text-emerald-300/80 uppercase tracking-wide mb-1 font-display">Cost</div>
                                                <div className="text-xl font-display font-bold text-white">
                                                    {session.sessionCost}
                                                </div>
                                                <div className="text-xs text-emerald-300 font-body">tokens</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Actions */}
                        <div className="flex flex-col justify-center gap-3 lg:min-w-[200px]">
                            {/* User Actions */}
                            {!AdminView && session.sessionBookText !== "Booked" && (
                                <button
                                    disabled={(session.availableSeats - session.seatsTaken) === 0 || differenceInHours(new Date(session.startDate), new Date()) < 24}
                                    onClick={() => setOpenAddBookingModal(true)}
                                    className={`${
                                        (session.availableSeats - session.seatsTaken) === 0 || differenceInHours(new Date(session.startDate), new Date()) < 24
                                            ? 'bg-luxury-graphite/50 text-accent-platinum/50 cursor-not-allowed border border-emerald-600/20 py-3 px-6 rounded-xl font-display font-semibold transition-all duration-300'
                                            : 'btn-primary'
                                    } flex items-center justify-center space-x-2`}
                                >
                                    {differenceInHours(new Date(session.startDate), new Date()) < 24 ? (
                                        <PhoneIcon className="h-5 w-5" />
                                    ) : (
                                        <PaperAirplaneIcon className="h-5 w-5" />
                                    )}
                                    <span>
                                        {(session.availableSeats - session.seatsTaken) === 0
                                            ? "Full"
                                            : differenceInHours(new Date(session.startDate), new Date()) < 24
                                                ? "Call to Book"
                                                : session.sessionBookText}
                                    </span>
                                </button>
                            )}
                            
                            {!AdminView && session.sessionBookText === "Booked" && canCancelBooking && (
                                <button
                                    onClick={() => setOpenCancelBookingModal(true)}
                                    className="btn-secondary  bg-orange-950/70 text-performance-pulse border-performance-pulse/50 hover:border-performance-pulse/80 hover:shadow-[0_0_30px_rgba(255,7,58,0.25)] flex items-center justify-center space-x-2"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                    <span>Cancel Session</span>
                                </button>
                            )}

                            {/* Admin Actions */}
                            {isAdmin && (
                                <div className="flex flex-col gap-2">
                                    <button
                                        onClick={() => setOpenManageSessionModal(true)}
                                        className="btn-secondary py-2 px-4 rounded-xl flex items-center justify-center space-x-2"
                                    >
                                        <Cog6ToothIcon className="h-4 w-4" />
                                        <span>Manage</span>
                                    </button>
                                    
                                    <button
                                        onClick={() => setOpenAddUserToSessionModal(true)}
                                        className="btn-secondary py-2 px-4 rounded-xl flex items-center justify-center space-x-2"
                                    >
                                        <UserPlusIcon className="h-4 w-4" />
                                        <span>Add User</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Desktop Attendees - Subtle overlay at bottom */}
                        {sessionUsers && sessionUsers.length > 0 && (
                            <div className="absolute bottom-4 left-4 right-4">
                                <div className="glass-luxury rounded-xl border border-emerald-600/20 overflow-hidden">
                                    <div className="px-4 py-2 bg-luxury-charcoal/50 border-b border-emerald-600/10">
                                        <h4 className="text-sm font-display font-semibold text-emerald-300/80 uppercase tracking-wide">Attendees ({sessionUsers.length})</h4>
                                    </div>
                                    <div className="p-3 max-h-24 overflow-y-auto scrollbar-luxury">
                                        <div className="flex flex-wrap gap-2">
                                            {sessionUsers.map((user) => (
                                                <span key={user.userId} className="badge-luxury text-xs">
                                                    {user.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            <PopupModelWrapper 
                isOpen={openAddBookingModal} 
                handleClose={handleCloseAddBookingModal}
                ModelContent={BookSession} 
                Values={{
                    title: session.title,
                    sessionId: session.sessionId,
                    sessionCost: session.sessionCost
                }}
            />
            
            <PopupModelWrapper 
                isOpen={openCancelBookingModal} 
                handleClose={handleCloseCancelBookingModal}
                ModelContent={ConfirmCancelSession}
                Values={{title: session.title, sessionId: session.sessionId}}
            />
            
            <PopupModelWrapper 
                isOpen={openManageSessionModal} 
                handleClose={handleCloseManageSessionModal}
                ModelContent={ManageSession} 
                Values={{
                    title: session.title,
                    sessionId: session.sessionId,
                    sessionDateTime: session.startDate
                }}
            />
            
            <PopupModelWrapper 
                isOpen={openAddUserToSessionModal} 
                handleClose={handleCloseAddUserToSessionModal}
                ModelContent={AddUserToSession} 
                Values={{
                    title: session.title,
                    sessionId: session.sessionId,
                    sessionDateTime: session.startDate
                }}
            />
        </>
    );
}

export default SessionView;
